import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

import { showNotification } from "../../../../../constants";
import { useAuth } from "../../../../../hooks";
import {
  createMenuButton,
  updateMenuButton,
} from "../../hooks/SalesPagesHook";

// A menu button is a simplified salesPages record adding an external link to the
// nav. This modal creates/edits one. See docs/SALES_PAGES_CMS.md.
const emptyForm = {
  title: "",
  href: "",
  order: "",
  openInNewTab: true,
  showInNav: true,
  enabled: true,
};

const isValidHref = (href) => /^(https?:\/\/|\/)/.test(href.trim());

const MenuButtonModal = ({ open, menuButton, nextOrder, onClose, onSaved }) => {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const isEdit = !!menuButton;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;
    if (menuButton) {
      setForm({
        title: menuButton.title || "",
        href: menuButton.href || "",
        order: menuButton.order ?? "",
        openInNewTab: menuButton.openInNewTab !== false,
        showInNav: menuButton.showInNav !== false,
        enabled: menuButton.enabled !== false,
      });
    } else {
      setForm({ ...emptyForm, order: nextOrder ?? "" });
    }
  }, [open, menuButton, nextOrder]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      showNotification("Please enter a title");
      return;
    }
    if (!form.href.trim()) {
      showNotification("Please enter a URL");
      return;
    }
    if (!isValidHref(form.href)) {
      showNotification("URL must start with http://, https:// or /");
      return;
    }
    if (form.order === "" || Number.isNaN(Number(form.order))) {
      showNotification("Please enter a valid order number");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        href: form.href.trim(),
        order: Number(form.order),
        openInNewTab: form.openInNewTab,
        showInNav: form.showInNav,
        enabled: form.enabled,
      };
      if (isEdit) {
        await updateMenuButton(menuButton.id, payload, userId);
        showNotification("Menu button updated");
      } else {
        await createMenuButton(payload, userId);
        showNotification("Menu button created");
      }
      onSaved();
    } catch (error) {
      console.log("Error saving menu button:", error.message);
      showNotification("Error saving menu button");
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={titleStyles}>
        {isEdit ? "Edit Menu Button" : "Add Menu Button"}
      </DialogTitle>
      <DialogContent>
        <Typography sx={labelStyles}>Title *</Typography>
        <TextField
          fullWidth
          size="small"
          value={form.title}
          onChange={(e) => setField("title", e.target.value)}
          sx={{ mb: 3 }}
        />

        <Typography sx={labelStyles}>URL *</Typography>
        <Typography sx={helperStyles}>
          External link (https://…) or an internal path (/…).
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={form.href}
          onChange={(e) => setField("href", e.target.value)}
          sx={{ mb: 3 }}
        />

        <Typography sx={labelStyles}>Order *</Typography>
        <TextField
          fullWidth
          size="small"
          type="number"
          value={form.order}
          onChange={(e) => setField("order", e.target.value)}
          sx={{ mb: 3, maxWidth: 200 }}
        />

        <div style={{ display: "flex", flexDirection: "column" }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.openInNewTab}
                onChange={(e) => setField("openInNewTab", e.target.checked)}
              />
            }
            label="Open in new tab"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.showInNav}
                onChange={(e) => setField("showInNav", e.target.checked)}
              />
            }
            label="Show in navigation"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.enabled}
                onChange={(e) => setField("enabled", e.target.checked)}
              />
            }
            label="Enabled"
          />
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} style={cancelStyles}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleSave}
          style={saveStyles}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default MenuButtonModal;

const titleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 700,
};

const labelStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 14,
  color: "#0A0A0AAB",
  mb: 1,
};

const helperStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 12,
  color: "#808080",
  mb: 1,
};

const cancelStyles = {
  textTransform: "none",
  color: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};

const saveStyles = {
  textTransform: "none",
  background: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
